import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  Button,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useAppDispatch } from "../../app/hooks";
import {
  BackgroundProcessObjectType,
  BackgroundProcessSummaryDto,
  loadBackgroundProcessSummaryById,
  loadBackgroundProcessSummaryForObject,
} from "./backgroundProcessingApi";
import {
  isFinalStatus,
  isRunningStatus,
  statusHexColor as getStatusColor,
} from "./backgroundProcessStatus";

interface Props {
  open: boolean;
  onClose: () => void;
  objectType?: BackgroundProcessObjectType;
  objectId?: string;
  processId?: string;
  onCompleted?: (status: string) => void | Promise<void>;
}

export const BackgroundProcessSummaryDialog: React.FC<Props> = ({
  open,
  onClose,
  objectType,
  objectId,
  processId,
  onCompleted,
}) => {
  const dispatch = useAppDispatch();
  const [data, setData] = useState<BackgroundProcessSummaryDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState<string>("");
  const [pollingActive, setPollingActive] = useState(true);
  const hasDispatchedCompletionEvent = useRef(false);

  const fetchData = async () => {
    setError(null);

    try {
      const result = processId
        ? await dispatch(loadBackgroundProcessSummaryById(processId))
        : await dispatch(
            loadBackgroundProcessSummaryForObject({
              objectType: objectType ?? "DOCUMENT",
              objectId: objectId ?? "",
            })
          );

      if (
        loadBackgroundProcessSummaryById.fulfilled.match(result) ||
        loadBackgroundProcessSummaryForObject.fulfilled.match(result)
      ) {
        const payload = result.payload;
        setData(payload);

        if (isFinalStatus(payload.status)) {
          setPollingActive(false); // stop polling if final

          if (!hasDispatchedCompletionEvent.current) {
            hasDispatchedCompletionEvent.current = true;
            void Promise.resolve(onCompleted?.(payload.status)).catch(() => undefined);
          }
        }
      } else {
        setError("Failed to load process data.");
      }
    } catch {
      setError("An error occurred while loading process data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    hasDispatchedCompletionEvent.current = false;
    setPollingActive(true);
    setData(null);
    setLoading(true);
  }, [open, objectType, objectId, processId]);

  useEffect(() => {
    if (!open || !pollingActive) return;

    fetchData(); // initial fetch

    const interval = setInterval(() => {
      fetchData();
    }, 10000); // every 10s

    return () => clearInterval(interval);
  }, [open, objectType, objectId, processId, pollingActive]);

  useEffect(() => {
    if (!data?.startedAt) return;

    let interval: NodeJS.Timeout;

    const updateElapsed = () => {
      const start = new Date(data.startedAt);
      const end = data.completedAt ? new Date(data.completedAt) : new Date();

      const diffMs = end.getTime() - start.getTime();
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs / (1000 * 60)) % 60);
      const seconds = Math.floor((diffMs / 1000) % 60);

      setElapsedTime(
        `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      );
    };

    updateElapsed();

    if (!data.completedAt) {
      interval = setInterval(updateElapsed, 1000);
    }

    return () => clearInterval(interval);
  }, [data?.startedAt, data?.completedAt]);

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === "backdropClick") {
      return;
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <DialogTitle>Process progress</DialogTitle>

      <DialogContent dividers>
        {loading && !data && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {data && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Process #{data.processNumber} – {data.operationType}
            </Typography>

            <Typography variant="body2" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Status:
              <Box component="span" sx={{ fontWeight: "bold", color: getStatusColor(data.status) }}>
                {data.status}
              </Box>
              {isRunningStatus(data.status) && (
                <CircularProgress size={14} thickness={5} />
              )}
            </Typography>

            <Typography variant="body2" gutterBottom>
              Progress: {data.progressCurrent} / {data.progressTotal} ({data.progressPercent}%)
            </Typography>

            <Box mt={1} mb={2}>
              <LinearProgress
                variant="determinate"
                value={data.progressPercent}
                sx={{
                  height: 10,
                  borderRadius: 2,
                  [`& .MuiLinearProgress-bar`]: {
                    backgroundColor: getStatusColor(data.status),
                  },
                }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" display="block">
              Started:{" "}
              {new Date(data.startedAt).toLocaleString("en-US", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </Typography>

            {data.completedAt && (
              <Typography variant="caption" color="text.secondary" display="block">
                Completed:{" "}
                {new Date(data.completedAt).toLocaleString("en-US", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </Typography>
            )}

            <Typography
              sx={{ marginTop: 2, display: "flex", alignItems: "center", gap: 1 }}
              variant="body2"
              color="textSecondary"
            >
              <AccessTimeIcon fontSize="small" />
              Elapsed time: {elapsedTime}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {data && isRunningStatus(data.status) && (
          <Button onClick={onClose} variant="contained">
            Run in background
          </Button>
        )}
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
